import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum, WorkOrderChangeObjectIdInput } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getWhere } from 'pages/Project/Common/AddWorkOrderPriceForm/TabContent/PriceTable/hooks/getWhere';

import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { uerWorkOrderChangeUersSelectionListGql } from '../workOrderChangeAddPricesQueries';

export const useWorkOrderChangeTableRows = (props: {
    workOrderChangeId: number;
    constructionTypeId: number;
    chapter: ChapterEnum | undefined;
    objectIds: WorkOrderChangeObjectIdInput[];
}) => {
    const { chapter, constructionTypeId, workOrderChangeId, objectIds } = props;

    const [queryWhere, setQueryWhere] = useState<Record<string, any>>({});
    const [searchString, setSearchString] = useState('');

    const onSearch = useCallback(({ value }) => {
        setQueryWhere((currentFilters) => ({ ...currentFilters, searchString: value }));
    }, []);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setQueryWhere((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(onSearch, 500), [onSearch]);

    const onSearchStringChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const onSearchStringClear = useCallback(() => {
        if (searchString) {
            setSearchString('');

            setQueryWhere((prevFilters) => ({
                ...prevFilters,
                searchString: undefined,
            }));
        }
    }, [searchString]);

    const where = useMemo(() => getWhere(chapter, queryWhere), [chapter, queryWhere]);

    const variables = useMemo(() => {
        if (constructionTypeId && workOrderChangeId && chapter) {
            return {
                workOrderChangeId,
                constructionTypeId,
                objectIds,
                chapter,
                where,
            };
        }

        return undefined;
    }, [chapter, constructionTypeId, objectIds, where, workOrderChangeId]);

    const [data, , , request] = useRequest({
        requestId: 'uerWorkOrderChangeUerSelectionList',
        query: uerWorkOrderChangeUersSelectionListGql,
        withEffect: false,
        variables,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'uerWorkOrderUerSelectionList',
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (data?.uerWorkOrderChangeUersSelectionList?.items && getRowParams?.current) {
            const items = data?.uerWorkOrderChangeUersSelectionList?.items;

            getRowParams?.current?.successCallback(items, data?.uerWorkOrderChangeUersSelectionList?.totalCount);
        }
    }, [
        data?.uerWorkOrderChangeUersSelectionList?.items,
        data?.uerWorkOrderChangeUersSelectionList?.totalCount,
        getRowParams,
    ]);

    return {
        dataSource,
        onSearchStringClear,
        onSearchStringChange,
        searchString,
        onSearch,
        filters: queryWhere,
        changeFilterHandler,
    };
};
